import { useEffect, useRef, type RefObject } from 'react'
import * as Cesium from 'cesium'
import {
  applyDiffCloudVisibility,
  applyDiffTilesetVisibility,
  authToken,
  buildDiffCloud,
  buildVoxelCloud,
  KOREA,
  type CdActiveDiff,
} from './cdVoxelViz'
import type { CdObservation } from '../../types/changeDetection'

/** A/B 두 관측의 원본 3D Tiles 를 blink(교대 점멸) 또는 split(화면 분할)으로 비교 */
export interface CdCompare {
  mode: 'blink' | 'split'
  sourceObsId: number
  targetObsId: number
}

interface Args {
  viewerRef: RefObject<Cesium.Viewer | null>
  /** 변화탐지 탭이 활성일 때만 레이어를 관리한다 */
  enabled: boolean
  observations: CdObservation[]
  originalVisibleIds: number[]
  voxelVisibleIds: number[]
  activeDiff: CdActiveDiff | null
  center: { lon: number; lat: number } | null
  compare: CdCompare | null
  /** split 모드 분할선 위치 (0~1) */
  splitPosition: number
}

function tilesetResource(url: string): Cesium.Resource {
  const proxied = url.startsWith('/')
  const token = authToken()
  return new Cesium.Resource({
    url: proxied ? `${window.location.origin}${url}` : url,
    headers: proxied && token ? { Authorization: `Bearer ${token}` } : {},
  })
}

/**
 * zoomTo/flyTo 대상으로 안전한 boundingSphere 인지 검사한다.
 *  - voxel/diff 타일이 지오레퍼런싱 없이(로컬 좌표) 나오면 중심이 지구 중심(0,0,0) 근처가 되어
 *    카메라가 널 아일랜드/우주로 튄다.
 *  - diff 결과 타일이 깨진 bounding volume(반경 수천 km, 고도 수백 km)으로 나오는 사례도 있어
 *    단일 현장으로는 비현실적인 반경도 배제한다.
 */
function isSaneSphere(s: Cesium.BoundingSphere | undefined): boolean {
  if (!s) return false
  const c = s.center
  if (![c.x, c.y, c.z, s.radius].every(Number.isFinite)) return false
  if (s.radius <= 0 || s.radius > 3.0e5) return false // 반경 300km 초과면 단일 현장이 아님
  const mag = Math.hypot(c.x, c.y, c.z)
  return mag > 1.0e6 // 지구 중심 근처면 위치정보 없는 로컬 좌표
}

/**
 * 대시보드의 공유 Cesium 뷰어 위에 변화탐지 레이어(원본 3D Tiles / voxel / diff 결과)를 명령형으로
 * 얹고 걷어낸다. 뷰어 생성·파괴에는 관여하지 않는다.
 * voxel·diff 결과는 실제 3D Tiles URL(2단계)이 있으면 로드하고, 없으면 체적값 기반 합성 미리보기를 그린다.
 */
export function useCdMapLayers({
  viewerRef,
  enabled,
  observations,
  originalVisibleIds,
  voxelVisibleIds,
  activeDiff,
  center,
  compare,
  splitPosition,
}: Args) {
  const tilesetsRef = useRef<Map<number, Cesium.Cesium3DTileset>>(new Map())
  const loadingRef = useRef<Set<number>>(new Set())
  const voxelSyntheticRef = useRef<Map<number, Cesium.Entity[]>>(new Map())
  const voxelRealRef = useRef<Map<number, Cesium.Cesium3DTileset>>(new Map())
  const voxelLoadingRef = useRef<Set<number>>(new Set())
  const diffSyntheticRef = useRef<Cesium.Entity[]>([])
  const diffRealRef = useRef<Cesium.Cesium3DTileset[]>([])
  const anchorRef = useRef<Cesium.Cartographic | null>(null)
  const blinkTimerRef = useRef<number | null>(null)

  // 비동기 타일 로드 콜백이 최신 표시여부를 참조하도록 ref 로 들고 있는다.
  const diffVisibleRef = useRef(activeDiff?.visible)
  diffVisibleRef.current = activeDiff?.visible

  // 원본 3D Tiles 정리 루프가 비교 중인 관측 타일을 제거하지 않도록 보호한다.
  const compareIdsRef = useRef<Set<number>>(new Set())
  compareIdsRef.current = compare ? new Set([compare.sourceObsId, compare.targetObsId]) : new Set()

  const stopBlink = () => {
    if (blinkTimerRef.current != null) {
      window.clearInterval(blinkTimerRef.current)
      blinkTimerRef.current = null
    }
  }

  const clearAll = () => {
    const v = viewerRef.current
    stopBlink()
    if (v && !v.isDestroyed()) {
      tilesetsRef.current.forEach((ts) => {
        if (!ts.isDestroyed()) ts.splitDirection = Cesium.SplitDirection.NONE
      })
      v.scene.splitPosition = 0.0
      tilesetsRef.current.forEach((ts) => !ts.isDestroyed() && v.scene.primitives.remove(ts))
      voxelRealRef.current.forEach((ts) => !ts.isDestroyed() && v.scene.primitives.remove(ts))
      diffRealRef.current.forEach((ts) => !ts.isDestroyed() && v.scene.primitives.remove(ts))
      voxelSyntheticRef.current.forEach((ents) => ents.forEach((e) => v.entities.remove(e)))
      diffSyntheticRef.current.forEach((e) => v.entities.remove(e))
    }
    tilesetsRef.current.clear()
    voxelRealRef.current.clear()
    voxelSyntheticRef.current.clear()
    diffRealRef.current = []
    diffSyntheticRef.current = []
    anchorRef.current = null
    loadingRef.current.clear()
    voxelLoadingRef.current.clear()
  }

  const anchorFor = (obsId?: number): Cesium.Cartographic => {
    const candidates: (Cesium.Cesium3DTileset | undefined)[] = []
    if (obsId != null) candidates.push(tilesetsRef.current.get(obsId), voxelRealRef.current.get(obsId))
    candidates.push(...tilesetsRef.current.values(), ...voxelRealRef.current.values(), ...diffRealRef.current)
    for (const ts of candidates) {
      if (ts && !ts.isDestroyed()) return Cesium.Cartographic.fromCartesian(ts.boundingSphere.center)
    }
    if (anchorRef.current) return anchorRef.current
    if (center) return Cesium.Cartographic.fromDegrees(center.lon, center.lat, 0)
    return KOREA
  }

  const obsVoxelUrl = (id: number): string | null =>
    observations.find((o) => o.id === id)?.voxelTilesetUrl ?? null

  /**
   * 합성 diff 미리보기를 얹을 지리 좌표를 구한다. 프로젝트 centerLon/Lat 는 UI 에서
   * 입력받지 않아 보통 비어 있으므로, diff 가 비교한 관측의 실제 3D Tiles 위치를 우선한다.
   *  1) 이미 로드된 원본/voxel 타일 (해당 관측 우선)
   *  2) 관측의 원본 tileset 을 헤드리스로 열어 boundingSphere 중심만 취득
   *  3) anchorFor() 폴백 (anchorRef → project center → KOREA)
   */
  const resolveDiffAnchor = async (obsIds: number[]): Promise<Cesium.Cartographic> => {
    for (const id of obsIds) {
      const ts = tilesetsRef.current.get(id) ?? voxelRealRef.current.get(id)
      if (ts && !ts.isDestroyed() && isSaneSphere(ts.boundingSphere)) {
        return Cesium.Cartographic.fromCartesian(ts.boundingSphere.center)
      }
    }
    for (const id of obsIds) {
      const url = observations.find((o) => o.id === id)?.originalTilesetUrl
      if (!url) continue
      try {
        const probe = await Cesium.Cesium3DTileset.fromUrl(tilesetResource(url))
        const sphere = Cesium.BoundingSphere.clone(probe.boundingSphere)
        try {
          probe.destroy()
        } catch {
          /* ignore */
        }
        if (isSaneSphere(sphere)) {
          anchorRef.current = Cesium.Cartographic.fromCartesian(sphere.center)
          return anchorRef.current
        }
      } catch {
        /* 다음 관측 시도 */
      }
    }
    return anchorFor()
  }

  /** origin(경위도) 상공으로 카메라를 확정적으로 이동한다. viewer.flyTo(entities) 는
   *  엔티티 boundingSphere 준비 타이밍에 의존해 조용히 실패하는 경우가 있어 쓰지 않는다. */
  const flyToAnchor = (origin: Cesium.Cartographic) => {
    const vv = viewerRef.current
    if (!vv || vv.isDestroyed()) return
    const center = Cesium.Cartesian3.fromRadians(origin.longitude, origin.latitude, origin.height || 0)
    vv.camera.flyToBoundingSphere(new Cesium.BoundingSphere(center, 120), { duration: 1.2 })
  }

  const reconcileVoxel = () => {
    const v = viewerRef.current
    if (!v || v.isDestroyed()) return
    const want = new Set(voxelVisibleIds)

    for (const [id, ents] of voxelSyntheticRef.current) {
      if (!want.has(id) || obsVoxelUrl(id)) {
        ents.forEach((e) => v.entities.remove(e))
        voxelSyntheticRef.current.delete(id)
      }
    }
    for (const [id, ts] of voxelRealRef.current) {
      if (!want.has(id) || !obsVoxelUrl(id)) {
        if (!ts.isDestroyed()) v.scene.primitives.remove(ts)
        voxelRealRef.current.delete(id)
      }
    }

    want.forEach(async (id) => {
      const url = obsVoxelUrl(id)
      if (url) {
        if (voxelRealRef.current.has(id) || voxelLoadingRef.current.has(id)) return
        voxelLoadingRef.current.add(id)
        try {
          const ts = await Cesium.Cesium3DTileset.fromUrl(tilesetResource(url))
          const vv = viewerRef.current
          if (!vv || vv.isDestroyed() || !new Set(voxelVisibleIds).has(id)) return
          vv.scene.primitives.add(ts)
          voxelRealRef.current.set(id, ts)
          // 같은 관측의 원본 타일이 이미 화면을 잡고 있으면 카메라를 건드리지 않는다.
          // 그 외에는 voxel 타일의 boundingSphere 가 정상일 때만 이동한다.
          const originalTs = tilesetsRef.current.get(id)
          const originalShown = !!originalTs && !originalTs.isDestroyed()
          if (originalShown) {
            // 이미 원본 위치에 있음 — 이동 생략
          } else if (isSaneSphere(ts.boundingSphere)) {
            vv.zoomTo(ts)
          } else {
            console.warn('voxel 타일 boundingSphere 비정상 — 카메라 이동 생략 (지오레퍼런싱 확인 필요)',
              id, ts.boundingSphere)
          }
        } catch (e) {
          console.error('voxel 타일 로드 실패', e)
        } finally {
          voxelLoadingRef.current.delete(id)
        }
      } else if (!voxelSyntheticRef.current.has(id)) {
        voxelSyntheticRef.current.set(id, buildVoxelCloud(v, anchorFor(id)))
      }
    })
  }

  // 탭 비활성/언마운트 시 정리
  useEffect(() => {
    if (!enabled) clearAll()
    return () => clearAll()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled])

  // 원본 3D Tiles
  useEffect(() => {
    if (!enabled) return
    const v = viewerRef.current
    if (!v || v.isDestroyed()) return
    const want = new Set(originalVisibleIds)

    for (const [id, ts] of tilesetsRef.current) {
      if (!want.has(id) && !compareIdsRef.current.has(id)) {
        if (!ts.isDestroyed()) v.scene.primitives.remove(ts)
        tilesetsRef.current.delete(id)
      }
    }
    want.forEach(async (id) => {
      if (tilesetsRef.current.has(id) || loadingRef.current.has(id)) return
      const obs = observations.find((o) => o.id === id)
      if (!obs?.originalTilesetUrl) return
      loadingRef.current.add(id)
      try {
        const ts = await Cesium.Cesium3DTileset.fromUrl(tilesetResource(obs.originalTilesetUrl))
        const vv = viewerRef.current
        if (!vv || vv.isDestroyed() || !new Set(originalVisibleIds).has(id)) return
        vv.scene.primitives.add(ts)
        tilesetsRef.current.set(id, ts)
        if (!anchorRef.current) anchorRef.current = Cesium.Cartographic.fromCartesian(ts.boundingSphere.center)
        vv.zoomTo(ts)
        reconcileVoxel()
      } catch (e) {
        console.error('변화탐지 원본 타일 로드 실패', e)
      } finally {
        loadingRef.current.delete(id)
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, originalVisibleIds, observations])

  // voxel (실 타일 또는 합성)
  useEffect(() => {
    if (!enabled) return
    reconcileVoxel()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, voxelVisibleIds, observations])

  // 변화탐지 결과 (실 타일 또는 합성)
  // 3초 폴링이 diffs 배열을 갈아끼워도 내용이 같으면 재실행하지 않도록 시그니처로 게이팅한다.
  const diffSig = activeDiff
    ? JSON.stringify([
        activeDiff.diffId,
        activeDiff.added,
        activeDiff.removed,
        activeDiff.changed,
        activeDiff.realTilesets,
        activeDiff.obsIds,
      ])
    : null

  useEffect(() => {
    if (!enabled) return
    const v = viewerRef.current
    if (!v || v.isDestroyed()) return

    diffSyntheticRef.current.forEach((e) => v.entities.remove(e))
    diffSyntheticRef.current = []
    diffRealRef.current.forEach((ts) => !ts.isDestroyed() && v.scene.primitives.remove(ts))
    diffRealRef.current = []
    if (!activeDiff) return

    let cancelled = false

    const obsIds = activeDiff.obsIds

    if (activeDiff.realTilesets.length > 0) {
      let framed = false
      activeDiff.realTilesets.forEach(async (url, i) => {
        try {
          const ts = await Cesium.Cesium3DTileset.fromUrl(tilesetResource(url))
          const vv = viewerRef.current
          if (cancelled || !vv || vv.isDestroyed()) return
          vv.scene.primitives.add(ts)
          diffRealRef.current.push(ts)
          applyDiffTilesetVisibility(ts, diffVisibleRef.current ?? activeDiff.visible)
          if (i !== 0 || framed) return
          framed = true
          // 결과 타일의 boundingSphere 는 voxelizer 지오레퍼런싱에 따라 엉뚱할 수 있으므로
          // (반경 수천 km 로 나오는 사례 확인) 관측 위치(resolveDiffAnchor)를 기준으로 카메라를 옮긴다.
          if (!isSaneSphere(ts.boundingSphere)) {
            console.warn(
              '[변화탐지] 결과 타일 boundingSphere 가 비정상 — voxelizer 산출물 지오레퍼런싱 확인 필요',
              { radius: ts.boundingSphere?.radius },
            )
          }
          const origin = await resolveDiffAnchor(obsIds)
          if (cancelled) return
          const isKoreaFallback =
            Math.abs(origin.longitude - KOREA.longitude) < 1e-6 &&
            Math.abs(origin.latitude - KOREA.latitude) < 1e-6
          if (isKoreaFallback && isSaneSphere(ts.boundingSphere)) {
            vv.zoomTo(ts)
          } else {
            flyToAnchor(origin)
          }
        } catch (e) {
          console.error('변화탐지 결과 타일 로드 실패', e)
        }
      })
      return () => {
        cancelled = true
      }
    }

    void (async () => {
      const origin = await resolveDiffAnchor(obsIds)
      const vv = viewerRef.current
      if (cancelled || !vv || vv.isDestroyed()) return
      const ents = buildDiffCloud(vv, origin, activeDiff)
      diffSyntheticRef.current = ents
      flyToAnchor(origin)
    })()

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, diffSig])

  // 추가/제거/변경 복셀 표시여부 토글 — 타일을 다시 로드하지 않고 이미 얹힌
  // 합성 엔티티/실 타일셋에만 반영한다.
  const visSig = activeDiff ? JSON.stringify(activeDiff.visible) : null
  useEffect(() => {
    if (!enabled || !activeDiff) return
    const v = viewerRef.current
    if (!v || v.isDestroyed()) return
    applyDiffCloudVisibility(diffSyntheticRef.current, activeDiff.visible)
    diffRealRef.current.forEach((ts) => applyDiffTilesetVisibility(ts, activeDiff.visible))
    v.scene.requestRender()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, visSig, diffSig])

  // ── A/B 비교 보기 (blink / split) ─────────────────────────────────────────
  // 두 관측의 원본 3D Tiles 를 로드해 교대 점멸하거나 화면을 좌/우로 분할한다.
  const ensureOriginal = async (obsId: number): Promise<Cesium.Cesium3DTileset | null> => {
    const existing = tilesetsRef.current.get(obsId)
    if (existing && !existing.isDestroyed()) return existing
    if (loadingRef.current.has(obsId)) return null
    const obs = observations.find((o) => o.id === obsId)
    if (!obs?.originalTilesetUrl) return null
    loadingRef.current.add(obsId)
    try {
      const ts = await Cesium.Cesium3DTileset.fromUrl(tilesetResource(obs.originalTilesetUrl))
      const vv = viewerRef.current
      if (!vv || vv.isDestroyed()) {
        try {
          ts.destroy()
        } catch {
          /* ignore */
        }
        return null
      }
      vv.scene.primitives.add(ts)
      tilesetsRef.current.set(obsId, ts)
      return ts
    } catch (e) {
      console.error('비교 원본 타일 로드 실패', e)
      return null
    } finally {
      loadingRef.current.delete(obsId)
    }
  }

  const compareSig = compare ? `${compare.mode}:${compare.sourceObsId}:${compare.targetObsId}` : null

  useEffect(() => {
    const v = viewerRef.current
    if (!v || v.isDestroyed()) return
    stopBlink()

    // 비활성/해제: split 방향·분할선 원복, show 는 originalVisibleIds 기준으로 되돌림
    if (!enabled || !compare) {
      const want = new Set(originalVisibleIds)
      tilesetsRef.current.forEach((ts, id) => {
        if (ts.isDestroyed()) return
        ts.splitDirection = Cesium.SplitDirection.NONE
        ts.show = want.has(id)
      })
      v.scene.splitPosition = 0.0
      v.scene.requestRender()
      return
    }

    let cancelled = false
    void (async () => {
      const [src, tgt] = await Promise.all([
        ensureOriginal(compare.sourceObsId),
        ensureOriginal(compare.targetObsId),
      ])
      const vv = viewerRef.current
      if (cancelled || !vv || vv.isDestroyed() || !src || !tgt) return

      // 비교 대상 외의 원본은 잠시 숨긴다 (비교 화면 격리)
      tilesetsRef.current.forEach((ts, id) => {
        if (id !== compare.sourceObsId && id !== compare.targetObsId && !ts.isDestroyed()) ts.show = false
      })

      if (compare.mode === 'split') {
        src.splitDirection = Cesium.SplitDirection.LEFT
        tgt.splitDirection = Cesium.SplitDirection.RIGHT
        src.show = true
        tgt.show = true
        vv.scene.splitPosition = splitPosition
      } else {
        src.splitDirection = Cesium.SplitDirection.NONE
        tgt.splitDirection = Cesium.SplitDirection.NONE
        src.show = true
        tgt.show = false
        let showSrc = true
        blinkTimerRef.current = window.setInterval(() => {
          const a = tilesetsRef.current.get(compare.sourceObsId)
          const b = tilesetsRef.current.get(compare.targetObsId)
          if (!a || !b || a.isDestroyed() || b.isDestroyed()) return
          showSrc = !showSrc
          a.show = showSrc
          b.show = !showSrc
          viewerRef.current?.scene.requestRender()
        }, 700)
      }
      if (isSaneSphere(src.boundingSphere)) vv.zoomTo(src)
      vv.scene.requestRender()
    })()

    return () => {
      cancelled = true
      stopBlink()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, compareSig, observations])

  // split 분할선 위치 갱신
  useEffect(() => {
    const v = viewerRef.current
    if (!v || v.isDestroyed() || !compare || compare.mode !== 'split') return
    v.scene.splitPosition = splitPosition
    v.scene.requestRender()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compareSig, splitPosition])
}
